import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import Markets from "@/pages/Markets";
import Discover from "@/pages/Discover";
import Companies from "@/pages/Companies";
import Company from "@/pages/Company";
import Compare from "@/pages/Compare";
import Portfolio from "@/pages/Portfolio";
import Learn from "@/pages/Learn";
import Lesson from "@/pages/Lesson";
import { About, Contact, FAQ, NotFound, Privacy, Terms } from "@/pages/LaunchPages";
import { RouteMetadata } from "@/components/RouteMetadata";
import { AnalyticsConsent } from "@/components/AnalyticsConsent";

const ScrollTop = () => {
  const { pathname } = useLocation();
  React.useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

// Subtle, calm route-change transition — a short fade + rise, no bounce.
const AnimatedRoutes = () => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
      >
        <Routes location={location}>
          <Route path="/" element={<Markets />} />
          <Route path="/discover" element={<Discover />} />
          <Route path="/companies" element={<Companies />} />
          <Route path="/company/:ticker" element={<Company />} />
          <Route path="/compare" element={<Compare />} />
          <Route path="/portfolio" element={<Portfolio />} />
          <Route path="/learn" element={<Learn />} />
          <Route path="/learn/:slug" element={<Lesson />} />
          <Route path="/about" element={<About />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
};

function App() {
  return (
    <BrowserRouter>
      <ScrollTop />
      <RouteMetadata />
      <div className="relative min-h-screen bg-canvas text-white">
        <div className="lv-ambient" />
        <div className="lv-grain" />
        <div className="relative z-10">
          <a href="#main-content" className="lv-skip-link">Skip to main content</a>
          <Navbar />
          <main id="main-content" tabIndex="-1" className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8">
            <AnimatedRoutes />
          </main>
          <Footer />
        </div>
        <Toaster position="bottom-right" theme="dark" />
        <AnalyticsConsent />
      </div>
    </BrowserRouter>
  );
}

export default App;
